import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import VikiLoader from "@/components/VikiLoader";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  SquaresFour,
  ForkKnife,
  CookingPot,
  BowlFood,
  Package,
  Carrot,
  Warehouse,
  Receipt,
  UsersThree,
  SignOut,
  ArrowSquareOut,
  AddressBook,
  Gear,
  UserGear,
  List,
} from "@phosphor-icons/react";

const navItems = [
  { to: "/", label: "Dashboard", icon: SquaresFour, testId: "nav-dashboard", end: true },
  { to: "/clienti", label: "Clienti", icon: AddressBook, testId: "nav-clienti" },
  { to: "/menu", label: "Menu", icon: ForkKnife, testId: "nav-menu" },
  { to: "/piatti", label: "Piatti", icon: CookingPot, testId: "nav-piatti" },
  { to: "/bowl", label: "Bowl", icon: BowlFood, testId: "nav-bowl" },
  { to: "/combo", label: "Combo", icon: Package, testId: "nav-combo" },
  { to: "/ingredienti", label: "Ingredienti", icon: Carrot, testId: "nav-ingredienti" },
  { to: "/magazzino", label: "Magazzino", icon: Warehouse, testId: "nav-magazzino" },
  { to: "/vendite", label: "Vendite", icon: Receipt, testId: "nav-vendite" },
  { to: "/staff", label: "Staff", icon: UsersThree, testId: "nav-staff", adminOnly: true },
  { to: "/impostazioni", label: "Impostazioni", icon: Gear, testId: "nav-impostazioni", masterOnly: true },
  { to: "/utenti", label: "Utenti", icon: UserGear, testId: "nav-utenti", masterOnly: true },
];

const LogoBlock = () => (
  <div className="flex items-center gap-3 px-6 py-6 border-b border-[#E5E5E0]">
    <div className="h-10 w-10 rounded-xl bg-[#D95D39] flex items-center justify-center">
      <BowlFood size={22} weight="fill" className="text-white" />
    </div>
    <div>
      <p className="font-extrabold text-lg leading-tight" style={{ fontFamily: "Manrope" }}>RisoRice</p>
      <p className="text-[10px] text-[#6B7280] uppercase tracking-widest">Gestionale</p>
    </div>
  </div>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const visibleItems = navItems.filter(
    (item) => (!item.adminOnly || ["admin", "master"].includes(user?.role)) && (!item.masterOnly || user?.role === "master")
  );

  const renderNav = (onNavigate) => (
    <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
      {visibleItems.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={item.testId}
          onClick={onNavigate}
          className={({ isActive }) =>
            `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${
              isActive ? "bg-[#D95D39] text-white" : "text-[#1C1C1C] hover:bg-stone-100"
            }`
          }
        >
          <item.icon size={20} weight="duotone" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );

  const userBox = (
    <div className="px-4 py-5 border-t border-[#E5E5E0]">
      <p className="text-sm font-semibold truncate" data-testid="user-name">{user?.name || "Admin"}</p>
      <p className="text-xs text-[#6B7280] truncate mb-3" data-testid="user-email">{user?.email}</p>
      <Button
        variant="outline"
        size="sm"
        data-testid="logout-button"
        onClick={onLogout}
        className="w-full rounded-full gap-2 hover:bg-stone-50"
      >
        <SignOut size={16} /> Esci
      </Button>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#FAF9F6]">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-[#E5E5E0] bg-white flex-col fixed inset-y-0 z-30" data-testid="sidebar">
        <LogoBlock />
        {renderNav()}
        {userBox}
      </aside>

      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-[#E5E5E0] flex items-center justify-between px-4 py-3" data-testid="mobile-header">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-[#D95D39] flex items-center justify-center">
              <BowlFood size={20} weight="fill" className="text-white" />
            </div>
            <p className="font-extrabold text-base" style={{ fontFamily: "Manrope" }}>RisoRice</p>
          </div>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                aria-label="Apri menu"
                className="h-10 w-10 rounded-xl border border-[#E5E5E0] flex items-center justify-center hover:bg-stone-50 active:bg-stone-100"
              >
                <List size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 flex flex-col bg-white" data-testid="mobile-drawer">
              <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
              <LogoBlock />
              {renderNav(() => setMobileOpen(false))}
              {userBox}
            </SheetContent>
          </Sheet>
        </header>

        <main className="flex-1 min-w-0">
          <div className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
            <a
              href="https://areapaq.it/menu"
              data-testid="areapaq-top-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#6B7280] hover:text-[#D95D39] mb-4 transition-colors"
            >
              <ArrowSquareOut size={16} weight="duotone" /> AreaPAQ
            </a>
            <Outlet />
          </div>
        </main>
      </div>
      <VikiLoader />
    </div>
  );
}
